import { NEW_ENTRY_WINDOW_MS } from '../config/listingsConfig';

/**
 * Single source of truth for the over-the-image badge rules used on
 * feed-derived surfaces (UnifiedFeed, model listings under ProductDetail,
 * Listing Detail, Compare).
 *
 * Pure: no React, no styles. Returns a stable, render-ordered array of
 * { key, label, color, icon? }. Selection rules (in this exact order):
 *
 *   - SOLD short-circuits everything else.
 *   - MY LINK is shown alongside the rest.
 *   - TOP MATCH XOR NEW ENTRY (top match wins when both apply).
 *   - BEST DEAL when in cohort.
 *   - JUST LISTED when dom <= 7.
 *   - PRICE DROP only while the event is fresh (<= 6 days), direction down,
 *     amount negative.
 *
 * Cohort flags (TOP MATCH, BEST DEAL) are feed-relative and may be omitted
 * by surfaces that don't have a cohort (e.g. /listing/:id) — those badges
 * simply won't render in that context.
 *
 * @param {Object} listing - Feed entry with status, source, surfaced_at, viewed_at,
 *                           price_changed_at, price_change_direction, price_change_amount,
 *                           and a listing_snapshot.
 * @param {Object} [cohort] - { topMatchIds?: Set, bestPriceIds?: Set,
 *                              isTopMatch?: boolean, isBestDeal?: boolean }
 *                              The per-row booleans take precedence over the sets,
 *                              so callers that already did the lookup upstream can
 *                              pass them directly without rebuilding sets.
 * @returns {Array<{key: string, label: string, color: string, icon?: string}>}
 */
export function buildListingBadges(listing, cohort = {}) {
  if (!listing) return [];

  const isSold = listing.status && listing.status !== 'active';
  if (isSold) {
    return [{ key: 'sold', label: 'SOLD', color: '#FF0000' }];
  }

  const { topMatchIds, bestPriceIds, isTopMatch: topOverride, isBestDeal: bestOverride } = cohort;
  const feedEntryId = listing.id || listing.feed_entry_id;

  const isExternalUrl = listing.source === 'external_url';
  const isTopMatch = topOverride != null
    ? !!topOverride
    : !!(topMatchIds && feedEntryId != null && topMatchIds.has(feedEntryId));
  const isBestDeal = bestOverride != null
    ? !!bestOverride
    : !!(bestPriceIds && feedEntryId != null && bestPriceIds.has(feedEntryId));

  const isNewEntry = !isTopMatch
    && listing.viewed_at == null
    && listing.surfaced_at != null
    && (Date.now() - new Date(listing.surfaced_at).getTime()) <= NEW_ENTRY_WINDOW_MS;

  const dom = listing?.listing_snapshot?.dom;
  const isJustListed = dom != null && dom <= 7;

  const isRecentDrop = listing.price_changed_at
    && (Date.now() - new Date(listing.price_changed_at).getTime()) < 6 * 86400000;
  const hasPriceDrop = isRecentDrop
    && listing.price_change_direction === 'down'
    && listing.price_change_amount < 0;

  const badges = [];
  if (isExternalUrl) badges.push({ key: 'mylink', label: 'MY LINK', color: '#10B981' });
  if (isTopMatch) badges.push({ key: 'top', label: 'TOP MATCH', color: '#0556F7' });
  else if (isNewEntry) badges.push({ key: 'new', label: 'NEW ENTRY', color: '#FF7B00' });
  if (isBestDeal) badges.push({ key: 'best_deal', label: 'BEST DEAL', color: '#F59E0B', icon: 'star-circle' });
  if (isJustListed) badges.push({ key: 'just_listed', label: 'JUST LISTED', color: '#06B6D4' });
  if (hasPriceDrop) badges.push({ key: 'drop', label: 'PRICE DROP', color: '#16A34A', icon: 'arrow-down' });

  return badges;
}

/**
 * Compute the cohort sets that drive the TOP MATCH and BEST DEAL badges.
 *
 *   - TOP MATCH: per-model rank-1 by score among active listings with score >= 75.
 *   - BEST DEAL: per-model rank-1 by price_vs_market signal value (>= 75) among
 *                active listings.
 *
 * Pure: takes the loaded listings array, returns two Sets keyed by feed entry id.
 *
 * @param {Array} listings
 * @returns {{ topMatchIds: Set, bestPriceIds: Set }}
 */
export function computeBadgeCohorts(listings) {
  const empty = { topMatchIds: new Set(), bestPriceIds: new Set() };
  if (!Array.isArray(listings) || listings.length === 0) return empty;

  const bestScoreByModel = {};
  const bestPriceByModel = {};

  for (const l of listings) {
    if (!l || l.status !== 'active') continue;
    const id = l.id || l.feed_entry_id;
    if (id == null) continue;
    const modelId = l.model_id ?? '__no_model__';

    const score = Number(l.score ?? 0);
    if (score >= 75) {
      const current = bestScoreByModel[modelId];
      if (!current || score > current.score) {
        bestScoreByModel[modelId] = { id, score };
      }
    }

    const breakdown = Array.isArray(l.score_breakdown) ? l.score_breakdown : null;
    if (breakdown) {
      const priceEntry = breakdown.find((s) => s?.signal === 'price_vs_market');
      const value = priceEntry ? Number(priceEntry.value) : null;
      if (value != null && value >= 75) {
        const current = bestPriceByModel[modelId];
        if (!current || value > current.value) {
          bestPriceByModel[modelId] = { id, value };
        }
      }
    }
  }

  return {
    topMatchIds: new Set(Object.values(bestScoreByModel).map((b) => b.id)),
    bestPriceIds: new Set(Object.values(bestPriceByModel).map((b) => b.id)),
  };
}

export default buildListingBadges;
